import { useContext, useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useRouter } from "@tanstack/react-router";
import { QueryClientContext, QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { Home, MapPin, MessageCircle, RefreshCw } from "lucide-react";
import { PW, pewterPanelApproved, pewterPrimary } from "@majourneys/booking";
import { WHATSAPP_URL } from "@majourneys/shared";
import { HeaderSearch } from "../../../shared/layout/header-search";
import {
  ERROR_TONE_BY_STATUS,
  isKnownErrorStatus,
  normalizeErrorStatus,
  type HttpErrorStatus,
} from "../lib/http-errors";

type ErrorPageProps = {
  status: HttpErrorStatus | number;
  details?: string;
};

/** The cities a lost guest is offered: the four with the most to book. */
const POPULAR_CITIES = ["marrakech", "casablanca", "agadir", "fez"] as const;

/** A white control with one hairline: the secondary action and the city chips. */
const CONTROL = { background: PW.controlFill, boxShadow: `0 0 0 1px ${PW.controlEdge}`, color: PW.navy } as const;

/** Words for the chip on top of the card; the rest fall back to "Error 4xx". */
function chipKey(status: number): string | null {
  if (status === 404 || status === 410) return "errors.chip.not_found";
  if (status === 500 || status === 502 || status === 503 || status === 504) return "errors.chip.our_side";
  return null;
}

/**
 * The header search, on the error page.
 *
 * The root route's error boundary renders outside the app's providers, so the
 * query client may be missing there. The router carries the same client in
 * its context; failing that, the field is left out rather than crashing the
 * one page that has to render.
 */
function ErrorSearch() {
  const fromContext = useContext(QueryClientContext);
  const router = useRouter({ warn: false });
  const fromRouter = (router?.options.context as { queryClient?: QueryClient } | undefined)?.queryClient;
  const field = <HeaderSearch surface="control" listId="error-search-suggestions" />;
  if (fromContext) return field;
  if (fromRouter) return <QueryClientProvider client={fromRouter}>{field}</QueryClientProvider>;
  return null;
}

function PrimaryLink({ href, onClick, children }: { href?: string; onClick?: () => void; children: ReactNode }) {
  const className =
    "mj-press inline-flex min-h-[46px] w-full items-center justify-center gap-2 rounded-full px-5 text-[15px] font-semibold";
  return href ? (
    <a href={href} className={className} style={pewterPrimary}>
      {children}
    </a>
  ) : (
    <button type="button" onClick={onClick} className={className} style={pewterPrimary}>
      {children}
    </button>
  );
}

export function ErrorPage({ status, details }: ErrorPageProps) {
  const { t, i18n } = useTranslation();
  const normalizedStatus = normalizeErrorStatus(status);
  const displayStatus =
    Number.isFinite(status) && status >= 400 && status < 600 ? status : normalizedStatus;
  const tone = ERROR_TONE_BY_STATUS[normalizedStatus];
  const lang = i18n.resolvedLanguage || i18n.language || "en";
  const homeHref = `/${lang}/`;
  const statusKey = isKnownErrorStatus(displayStatus) ? `errors.pages.${displayStatus}` : "";
  const titleKey = statusKey ? `${statusKey}.title` : "errors.pages.generic.title";
  const messageKey = statusKey ? `${statusKey}.message` : "errors.pages.generic.message";
  const title = t(titleKey, { defaultValue: t("errors.pages.generic.title") });
  const message = t(messageKey, {
    defaultValue: t("errors.pages.generic.message"),
  });
  const chip = chipKey(displayStatus);

  useEffect(() => {
    document.title = `${displayStatus} ${title} | MaJourneys`;
  }, [displayStatus, title]);

  // A lost guest (404 and kin) gets a way forward: search and cities. A page
  // that failed on our side exists, so the useful action is to load it again.
  // A private page will not open on a retry, so it only offers home.
  const lost = tone === "lost";
  const canRetry = tone === "busy" || tone === "broken";

  return (
    <section className="relative isolate bg-bg-body px-4 py-10 sm:px-8 sm:py-16 lg:py-20">
      <div className="mx-auto w-full min-w-0 max-w-[560px]">
        <div className="p-5 sm:p-7" style={pewterPanelApproved(24)}>
          <span
            className="inline-flex rounded-full px-3 py-1 text-[12px] font-bold"
            style={{ background: PW.goldTint, color: PW.goldDark }}
          >
            {chip ? t(chip) : t("errors.eyebrow", { status: displayStatus })}
          </span>
          <h1
            className="mt-3 font-heading text-[clamp(1.5rem,5vw,2rem)] font-bold leading-tight"
            style={{ color: PW.navy, letterSpacing: "-0.02em" }}
          >
            {title}
          </h1>
          <p className="mt-2 text-[15px] leading-relaxed" style={{ color: PW.muted }}>
            {message}
          </p>
          {details ? (
            <p
              className="mt-3 break-words rounded-[12px] px-3.5 py-2.5 font-mono text-xs leading-5"
              style={{ background: PW.softTint, color: PW.muted }}
            >
              {details}
            </p>
          ) : null}

          {lost ? (
            <>
              <div className="mt-5">
                <ErrorSearch />
              </div>
              <p
                className="mt-5 text-[11px] font-bold uppercase tracking-[0.1em]"
                style={{ color: PW.muted }}
              >
                {t("errors.popular_cities")}
              </p>
              <ul className="mt-2 flex flex-wrap gap-2">
                {POPULAR_CITIES.map((slug) => (
                  <li key={slug}>
                    <a
                      href={`/${lang}/place/${slug}`}
                      className="mj-press inline-flex min-h-[36px] items-center gap-1.5 rounded-full px-3.5 text-[13.5px] font-semibold"
                      style={CONTROL}
                    >
                      <MapPin size={14} color={PW.goldDark} aria-hidden />
                      {t(`nav.city.${slug}`)}
                    </a>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <PrimaryLink href={homeHref}>{t("errors.actions.back_home")}</PrimaryLink>
              </div>
            </>
          ) : canRetry ? (
            <div className="mt-6 flex flex-col gap-2.5">
              <PrimaryLink onClick={() => window.location.reload()}>
                <RefreshCw size={16} aria-hidden />
                {t("errors.try_again")}
              </PrimaryLink>
              <a
                href={homeHref}
                className="mj-press inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full px-5 text-[14.5px] font-semibold"
                style={CONTROL}
              >
                <Home size={16} aria-hidden />
                {t("errors.actions.back_home")}
              </a>
            </div>
          ) : (
            <div className="mt-6">
              <PrimaryLink href={homeHref}>
                <Home size={16} aria-hidden />
                {t("errors.actions.back_home")}
              </PrimaryLink>
            </div>
          )}

          <p
            className="mt-6 flex items-center gap-2 border-t pt-4 text-[14px]"
            style={{ borderColor: PW.hairline, color: PW.muted }}
          >
            <MessageCircle size={17} color={PW.goldDark} className="shrink-0" aria-hidden />
            <span>
              {t("errors.stuck")}{" "}
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-[24px] items-center font-semibold underline underline-offset-2"
                style={{ color: PW.navy }}
              >
                {t("errors.whatsapp")}
              </a>
            </span>
          </p>
        </div>
      </div>
    </section>
  );
}
